<template>
  <div class="space-y-4">
    <section class="app-section">
      <div class="app-toolbar items-center justify-between">
        <h2 class="m-0 text-xl font-semibold">Пользователи</h2>
        <div class="flex flex-wrap items-center gap-3">
          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-muted-foreground">
              Категория:
            </label>
            <select
              v-model="categoryFilter"
              @change="loadUsers"
              class="h-9 rounded-md border border-border bg-transparent px-2 text-xs focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">Все</option>
              <option
                v-for="category in userCategories"
                :key="category.id"
                :value="category.name"
              >
                {{ category.name }}
              </option>
            </select>
          </div>
          <button class="button" @click="openModal(false)">Добавить пользователя</button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="app-table min-w-[980px]">
          <thead>
            <tr>
              <th>Фото</th>
              <th>Username</th>
              <th>Имя</th>
              <th>Позиция</th>
              <th>Категория</th>
              <th>Статус</th>
              <th>Суперюзер</th>
              <th>Видимость</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td>
                <img
                  :src="user.photo_url"
                  alt="Фото"
                  class="h-10 w-10 rounded-md border object-cover"
                />
              </td>
              <td class="font-medium">{{ user.username }}</td>
              <td>{{ user.title }}</td>
              <td>{{ user.description }}</td>
              <td>{{ user.category || '—' }}</td>
              <td>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  :class="user.is_active ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive'"
                >
                  {{ user.is_active ? 'Активен' : 'Деактивирован' }}
                </span>
              </td>
              <td>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  :class="user.is_superuser ? 'bg-accent/15 text-accent' : 'bg-muted text-muted-foreground'"
                >
                  {{ user.is_superuser ? 'Да' : 'Нет' }}
                </span>
              </td>
              <td>
                <span
                  class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  :class="user.visible ? 'bg-accent/15 text-accent' : 'bg-muted text-muted-foreground'"
                >
                  {{ user.visible ? 'Виден' : 'Скрыт' }}
                </span>
              </td>
              <td>
                <div class="flex flex-wrap gap-2">
                  <button class="button button-outline !h-8 !px-3 !py-0 text-xs" @click="openModal(true, user)">
                    Изменить
                  </button>
                  <button
                    v-if="canManageForeignCommercials"
                    class="button button-outline !h-8 !px-3 !py-0 text-xs"
                    @click="openCommercialEditor(user)"
                  >
                    КП
                  </button>
                  <button
                    v-if="isSuperuser && user.username !== currentUsername"
                    :class="user.is_active ? 'button button-danger !h-8 !px-3 !py-0 text-xs' : 'button button-success !h-8 !px-3 !py-0 text-xs'"
                    @click="handleDeactivate(user)"
                    :disabled="isProcessing"
                  >
                    {{ user.is_active ? 'Деактивировать' : 'Активировать' }}
                  </button>
                  <button
                    v-if="isSuperuser && user.username !== currentUsername"
                    class="button button-danger !h-8 !px-3 !py-0 text-xs"
                    @click="handleDelete(user)"
                    :disabled="isProcessing"
                  >
                    Удалить
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <UserModal
      :isVisible="isModalVisible"
      :user="selectedUser"
      :isEditMode="isEditMode"
      :isSuperuser="isSuperuser"
      @submit="handleUserSubmit"
      @close="closeModal"
      @refresh="loadUsers"
    />
  </div>
</template>

<script>
import {
  getUsersApi,
  deleteUserApi,
  deactivateUserApi,
  activateUserApi,
  getAdminUserCategoriesApi
} from '../services/api';
import UserModal from './UserModal.vue';
import { isUserAdminForUi } from '../utils/user-role-override';

export default {
  name: 'UsersView',
  props: {
    currentUser: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['edit-commercial'],
  components: {
    UserModal
  },
  data() {
    return {
      users: [],
      isModalVisible: false,
      selectedUser: {},
      isEditMode: false,
      isProcessing: false,
      userCategories: [],
      categoryFilter: ''
    }
  },
  computed: {
    effectiveCurrentUser() {
      return this.currentUser || {};
    },
    isSuperuser() {
      return Boolean(this.effectiveCurrentUser.is_superuser);
    },
    isAdmin() {
      return isUserAdminForUi(this.effectiveCurrentUser);
    },
    canManageForeignCommercials() {
      return this.isSuperuser || this.isAdmin;
    },
    currentUsername() {
      return this.effectiveCurrentUser.username || '';
    }
  },
  async mounted() {
    await this.loadUserCategories();
    await this.loadUsers();
  },
  methods: {
    async loadUsers() {
      // Загружаем всех пользователей включая неактивных для админки
      this.users = await getUsersApi(true, this.categoryFilter || null);
    },
    async loadUserCategories() {
      try {
        this.userCategories = await getAdminUserCategoriesApi();
      } catch (error) {
        console.error('Ошибка загрузки категорий пользователей:', error);
        this.userCategories = [];
      }
    },
    openModal(isEdit, user = {}) {
      this.isEditMode = isEdit;
      this.selectedUser = isEdit ? { ...user } : { username: '', title: '', description: '', category: '', password: '', long_description: '' };
      this.isModalVisible = true;
    },
    closeModal() {
      this.isModalVisible = false;
    },
    openCommercialEditor(user) {
      if (!this.canManageForeignCommercials || !user?.id) {
        return;
      }
      this.$emit('edit-commercial', {
        id: user.id,
        username: user.username,
        commercial_filled: Boolean(user.commercial_filled)
      });
    },
    async handleDeactivate(user) {
      if (!this.isSuperuser) return;
      
      if (!confirm(`${user.is_active ? 'Деактивировать' : 'Активировать'} пользователя ${user.title}?`)) {
        return;
      }
      
      this.isProcessing = true;
      try {
        if (user.is_active) {
          await deactivateUserApi(user.id);
        } else {
          await activateUserApi(user.id);
        }
        await this.loadUsers();
        alert(`Пользователь ${user.is_active ? 'деактивирован' : 'активирован'} успешно`);
      } catch (error) {
        console.error('Ошибка при изменении статуса пользователя:', error);
        alert('Ошибка при изменении статуса пользователя');
      } finally {
        this.isProcessing = false;
      }
    },
    async handleDelete(user) {
      if (!this.isSuperuser) return;
      
      if (!confirm(`Вы уверены, что хотите удалить пользователя ${user.title}? Это действие нельзя отменить.`)) {
        return;
      }
      
      this.isProcessing = true;
      try {
        await deleteUserApi(user.id);
        await this.loadUsers();
        alert('Пользователь удален успешно');
      } catch (error) {
        console.error('Ошибка при удалении пользователя:', error);
        alert(error.response?.data?.detail || 'Ошибка при удалении пользователя');
      } finally {
        this.isProcessing = false;
      }
    },
    async handleUserSubmit() {
      await this.loadUsers();
    }
  }
}
</script>
